if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Our stand-in for Shopify's Buy it now: same add-to-cart path, so the
        // photo and add-ons are captured, then straight to checkout.
        this.buyNowButton = this.querySelector('[data-glowframe-buy-now]');
        if (this.buyNowButton) {
          this.buyNowButton.addEventListener('click', () => {
            this.redirectToCheckout = true;
            this.form.requestSubmit();
          });
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        // A personalized product is useless without its photo — stop here rather
        // than letting an unprintable order through.
        const uploader = this.querySelector('glowframe-uploader');
        if (uploader && uploader.dataset.required === 'true' && !uploader.complete) {
          this.handleErrorMessage(window.glowframeStrings.photoRequired);
          // Clear the intent, or a later Add to cart would jump to checkout.
          this.redirectToCheckout = false;
          return;
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const addons = this.querySelector('glowframe-addons');
        const hasAddons = Boolean(addons && addons.hasSelection());
        // Ties the add-on lines to their parent so packing is unambiguous.
        const bundleId = hasAddons ? `gf-${Date.now().toString(36)}` : null;

        const sections = this.cart ? this.cart.getSectionsToRender().map((section) => section.id) : null;
        if (this.cart) this.cart.setActiveElement(document.activeElement);

        // The main item goes as multipart FormData, never JSON: that is the only
        // way the customer's photo reaches Shopify's own file storage.
        const formData = new FormData(this.form);
        if (bundleId) formData.append('properties[_bundle_id]', bundleId);
        // Sections re-render on the last request only, so the cart paints once.
        if (sections && !hasAddons) {
          formData.append('sections', sections);
          formData.append('sections_url', window.location.pathname);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) return response;
            if (!hasAddons) return response;

            // Shopify has now stored the photo; reuse its URL for the keychain
            // rather than asking the customer to upload the same image twice.
            const photoUrl = uploadedPhotoUrl(response);
            const addonConfig = fetchConfig('javascript');
            addonConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
            const payload = { items: addons.buildAddonItems(bundleId, photoUrl) };
            if (sections) {
              payload.sections = sections;
              payload.sections_url = window.location.pathname;
            }
            addonConfig.body = JSON.stringify(payload);

            const addonResponse = await fetch(`${routes.cart_add_url}`, addonConfig);
            const addonJson = await addonResponse.json();
            // A failed add-on must not discard the main item, which is already in
            // the cart — fall back to rendering the successful first response.
            if (!addonResponse.ok) {
              console.error('GlowFrame: add-ons could not be added.', addonJson);
              return response;
            }
            return addonJson;
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              this.redirectToCheckout = false;
              return;
            }

            // Everything is in the cart at this point, so it is safe to leave.
            if (this.redirectToCheckout) {
              const root = window.Shopify?.routes?.root || '/';
              window.location.href = `${root}checkout`;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.redirectToCheckout = false;
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );

  /**
   * Pulls the CDN link Shopify generated for a customer upload out of an
   * /cart/add.js response. Shopify marks these by putting the file under
   * /uploads/, which is also how Dawn's cart decides to render them as links.
   */
  function uploadedPhotoUrl(response) {
    const properties = response.properties || response.items?.[0]?.properties;
    if (!properties) return null;
    const match = Object.values(properties).find(
      (value) => typeof value === 'string' && value.includes('/uploads/')
    );
    return match || null;
  }
}
